import { useRef, useState } from 'react';
import { ImageIcon, UploadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../ui/ProductImage';
import { inputCls } from './styles';
import { MAX_IMAGE_BYTES, blobToDataUrl, isImageFile, isSafeImageSrc, resizeImage } from '../../utils/image';
type Props = {
  value: string;
  onChange: (url: string) => void;
  /** Packshots tonen we heel; sfeerbeelden vullen het vlak. */
  fit?: 'contain' | 'cover';
  aspect?: string;
};
const MAX_MB = MAX_IMAGE_BYTES / 1024 / 1024;

/**
 * Een foto kiezen: slepen, bladeren of een link plakken.
 *
 * De waarde wordt opgeslagen in het concept, dus nooit een `blob:`-URL: die is na herladen
 * (en op elk ander apparaat) dood. In de demo wordt de verkleinde foto een `data:`-URL.
 * Met de server is dit de enige plek die verandert: uploaden naar de mediabibliotheek
 * (`POST /admin/media`, LARAVEL.md) en de teruggegeven https-link bewaren.
 */
export function ImageField({
  value,
  onChange,
  fit = 'contain',
  aspect = 'aspect-square'
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const takeFile = async (file?: File) => {
    if (!file) return;
    if (!isImageFile(file)) {
      toast.error('Deze foto kan niet worden gebruikt', {
        description: `Kies een afbeelding van maximaal ${MAX_MB} MB.`
      });
      return;
    }
    setBusy(true);
    try {
      // PNG blijft PNG: packshots met een doorzichtige achtergrond worden anders zwart.
      const blob = await resizeImage(file, 1600, 0.85, file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png');
      onChange(await blobToDataUrl(blob));
    } catch {
      toast.error('Deze foto kan niet worden gebruikt', {
        description: 'Het bestand kon niet worden gelezen. Probeer een jpg of png, of maak een nieuwe foto.'
      });
    } finally {
      setBusy(false);
    }
  };
  const safe = !value || isSafeImageSrc(value);
  const uploaded = value.startsWith('data:') || value.startsWith('blob:');
  return <div data-id="element-5001" data-mp="32hhxaiy39pf8">
      <div onDragOver={e => {
      e.preventDefault();
      setOver(true);
    }} onDragLeave={() => setOver(false)} onDrop={e => {
      e.preventDefault();
      setOver(false);
      void takeFile(e.dataTransfer.files[0]);
    }} className={`relative flex ${aspect} w-full items-center justify-center overflow-hidden rounded-md border bg-shell transition-colors duration-150 ease-soft ${over ? 'border-clay bg-blush/40' : 'border-line'}`} data-id="element-5002" data-mp="32hqa6au7ouqf">
        {value && !safe ? <span className="px-4 text-center text-[13px] text-clay" data-id="element-5003" data-mp="3c9novrkfi4tw">Plak een link die begint met https://</span> : value ? <ProductImage src={value} fit={fit} loading="eager" className={`h-full w-full ${fit === 'contain' ? 'p-4' : ''}`} data-id="element-5004" data-mp="3h64aof45o73t" /> : <span className="flex flex-col items-center gap-2 px-4 text-center text-[13px] text-muted" data-id="element-5005" data-mp="25sinv0lrqhlg">
            <ImageIcon className="h-6 w-6 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-5006" data-mp="1xbi261bgde0u" />
            Sleep een foto hierheen
          </span>}
      </div>
      <div className="mt-2 flex min-w-0 gap-2" data-id="element-5007" data-mp="1tc94ildakw00">
        <input value={busy ? 'Foto verwerken…' : uploaded ? 'Geüploade foto' : value} onChange={e => onChange(e.target.value)} placeholder="Of plak een link naar een foto" aria-label="Link naar een foto" className={inputCls} disabled={uploaded || busy} data-id="element-5008" data-mp="27a7kfv79h0hm" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-white px-3 text-[13px] text-ink transition-colors duration-150 ease-soft hover:border-ink/40" data-id="element-5009" data-mp="19ns1eoz6rury">
          <UploadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5010" data-mp="vsxxuzsu5sc3" />
          Kies
          <span className="sr-only" data-id="element-5011" data-mp="qvfqveydpn6q"> een foto van je apparaat</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" tabIndex={-1} aria-hidden="true" onChange={e => void takeFile(e.target.files?.[0])} data-id="element-5012" data-mp="3qb9qgmabf7m0" />
      </div>
    </div>;
}